import React from "react";
import {
  Box,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ContactMailRoundedIcon from "@mui/icons-material/ContactMailRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import MusicNoteRoundedIcon from "@mui/icons-material/MusicNoteRounded";
import { SEGMENT_FIELD_TYPES } from "../segmentRegistry";
import { getValueAtDescriptorPath } from "../elementEditing";
import LandingCopyTextField from "../inspector/LandingCopyTextField";

const FIELD_ICONS = Object.freeze({
  "business.name": StorefrontRoundedIcon,
  "footer.tagline": StorefrontRoundedIcon,
  "footer.navigationTitle": HomeRoundedIcon,
  "footer.homeLabel": HomeRoundedIcon,
  "footer.productsLabel": Inventory2RoundedIcon,
  "navigation.aboutLabel": InfoRoundedIcon,
  "navigation.highlightsLabel": AutoAwesomeRoundedIcon,
  "footer.contactLabel": ContactMailRoundedIcon,
  "navigation.socialTitle": ShareRoundedIcon,
  "business.whatsapp": WhatsAppIcon,
  "business.socials.instagram": InstagramIcon,
  "business.socials.facebook": FacebookIcon,
  "business.socials.youtube": YouTubeIcon,
  "business.socials.linkedin": LinkedInIcon,
  "business.socials.tiktok": MusicNoteRoundedIcon,
  "business.socials.website": LanguageRoundedIcon,
});

function ownershipLabel(field) {
  if (!field) return "";
  if (["navigation.aboutLabel", "navigation.highlightsLabel"].includes(field.path)) return "Compartido con Navbar";
  if (field.shared) return "Dato compartido";
  return "Sólo Footer";
}

function FooterField({ field, form, baselineForm, disabled, onCommit }) {
  if (!field) return null;
  const Icon = FIELD_ICONS[field.path] || StorefrontRoundedIcon;
  const value = getValueAtDescriptorPath(form, field.path, field.defaultValue ?? "");
  const ownership = ownershipLabel(field);

  if (field.type === SEGMENT_FIELD_TYPES.BOOLEAN) {
    return (
      <Box sx={{ p: 1.1, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2.5 }}>
        <FormControlLabel
          control={<Switch checked={value !== false} disabled={disabled} onChange={(event) => onCommit?.(field.path, event.target.checked)} />}
          label={field.label}
        />
        {ownership ? <Chip size="small" label={ownership} variant="outlined" /> : null}
      </Box>
    );
  }

  const multiline = field.type === SEGMENT_FIELD_TYPES.TEXTAREA;
  return (
    <Box sx={{ p: 1.1, border: "1px solid rgba(25,36,47,.10)", borderRadius: 2.5, bgcolor: "#fff" }}>
      <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.8 }}>
        <Icon fontSize="small" />
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 950 }}>{field.label}</Typography>
        {ownership ? <Chip size="small" label={ownership} variant="outlined" /> : null}
      </Stack>
      <LandingCopyTextField
        fullWidth
        size="small"
        type={field.type === SEGMENT_FIELD_TYPES.EMAIL ? "email" : "text"}
        label={field.label}
        field={field}
        path={field.path}
        form={form}
        baselineForm={baselineForm}
        value={value ?? ""}
        disabled={disabled}
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        commitMode="change"
        onCommit={(nextValue) => onCommit?.(field.path, nextValue)}
      />
    </Box>
  );
}

export default function FooterElementEditor({ descriptor, form, baselineForm, disabled, onCommit }) {
  const mode = descriptor?.editorMode || "brand";
  const fields = descriptor?.fields || [];
  const title = mode === "navigation" ? "Navegación" : mode === "socials" ? "Redes sociales" : "Marca";
  const subtitle = mode === "navigation"
    ? "Los textos marcados como compartidos usan el mismo valor que Navbar."
    : mode === "socials"
      ? "Estas redes son datos globales del negocio; no se crean copias para el footer."
      : "El nombre del negocio es compartido. La descripción corta pertenece sólo al footer.";

  return (
    <Stack spacing={1.2}>
      <Typography variant="subtitle2" sx={{ fontWeight: 950 }}>{title}</Typography>
      <Typography variant="caption" sx={{ color: "rgba(25,36,47,.60)" }}>{subtitle}</Typography>
      {fields.map((field) => (
        <FooterField key={field.path} field={field} form={form} baselineForm={baselineForm} disabled={disabled} onCommit={onCommit} />
      ))}
    </Stack>
  );
}
